import React from 'react';
import { Box, Typography, Stack, Button } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { heritageColors } from '../../../../heritageTheme.ts';
import { formatShabbatTimeRule } from '../../shabbatZmanUtils';
import LeyningDisplay from '../../../shared/LeyningDisplay';

const Times = ({ shabbat, processedCustomTimes, drashaText, loading, error, onRetry, title = 'זמני שבת', showTitle = true, dayEntryTime, dayExitTime, showTimeRule = false, showEntryExit = true, shabbatLeyning, showLeyning, showHaftarah, leyningDisplayMode }) => {
  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography sx={{ color: heritageColors.navy }}>טוען זמני שבת...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography sx={{ color: '#D32F2F', mb: 2 }}>{error}</Typography>
        {onRetry && (
          <Button onClick={onRetry} variant="contained" sx={{ bgcolor: heritageColors.navy }}>
            נסה שוב
          </Button>
        )}
      </Box>
    );
  }

  if (!shabbat) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography sx={{ color: heritageColors.navy }}>אין נתוני שבת</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, direction: 'rtl', minWidth: '350px' }}>
      {/* Header */}
      {showTitle && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <AutoAwesomeIcon sx={{ color: heritageColors.gold, fontSize: 28 }} />
          <Typography variant="h5" sx={{ color: heritageColors.navy, fontWeight: 700, fontSize: '1.8rem' }}>
            {title}
          </Typography>
        </Box>
      )}

      {/* Parasha */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h3" sx={{ color: heritageColors.navy, fontWeight: 700, fontSize: '2.5rem' }}>
          {shabbat.meta?.parshaShowPrefix !== false ? 'פרשת ' : ''}{shabbat.meta?.parsha || shabbat.parsha || 'בהר - בחוקותי'}
        </Typography>
        {shabbat.meta?.parshaSubtitle && (
          <Typography sx={{ fontSize: '1.2rem', fontWeight: 700, color: heritageColors.navy, opacity: 0.75, mt: 0.5 }}>
            {shabbat.meta.parshaSubtitle}
          </Typography>
        )}
      </Box>

      {/* Leyning Display */}
      {(showLeyning || showHaftarah) && shabbatLeyning && (
        <Box sx={{
          mb: 3,
          p: 2,
          borderRadius: 2,
          bgcolor: heritageColors.creamLight,
          border: `1px solid ${heritageColors.gold}22`,
        }}>
          <LeyningDisplay
            leyning={shabbatLeyning}
            mode={leyningDisplayMode}
            showHaftarah={showHaftarah}
            showMaftir={leyningDisplayMode === 'full'}
            compact={false}
          />
        </Box>
      )}

      {/* Candle Lighting & Havdalah */}
      {showEntryExit && (
        <Box sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
        }}>
          <Box sx={{
            flex: 1,
            textAlign: 'center',
            py: 2.5,
            borderRadius: 3,
            bgcolor: heritageColors.gold,
          }}>
            <Typography sx={{ fontSize: '1.2rem', color: 'rgba(255,255,255,0.9)', mb: 0.5, fontWeight: 600 }}>
              כניסת שבת
            </Typography>
            <Typography sx={{
              fontSize: '2.5rem',
              fontWeight: 700,
              color: heritageColors.white,
            }}>
              {dayEntryTime || shabbat.meta?.candleLighting || shabbat.friday?.candleLighting || shabbat.candleLighting || '--:--'}
            </Typography>
          </Box>
          <Box sx={{
            flex: 1,
            textAlign: 'center',
            py: 2.5,
            borderRadius: 3,
            bgcolor: heritageColors.creamLight,
          }}>
            <Typography sx={{ fontSize: '1.2rem', color: heritageColors.slate, mb: 0.5, fontWeight: 600 }}>
              צאת שבת
            </Typography>
            <Typography sx={{
              fontSize: '2.5rem',
              fontWeight: 700,
              color: heritageColors.navy,
            }}>
              {dayExitTime || shabbat.meta?.havdalah || shabbat.shabbat?.havdalah || shabbat.havdalah || '--:--'}
            </Typography>
          </Box>
        </Box>
      )}

      {/* דרשת ליל שבת */}
      {drashaText && (
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography sx={{
            fontSize: '1.4rem',
            color: heritageColors.navy,
            fontWeight: 700,
            whiteSpace: 'pre-wrap',
          }}>
            {drashaText}
          </Typography>
        </Box>
      )}

      {/* Custom times */}
      {processedCustomTimes && processedCustomTimes.length > 0 && (
        <Stack spacing={1.5}>
          {/* קודם זמני יום שישי */}
          {processedCustomTimes
            .filter(ct => (ct.day || 'friday') === 'friday')
            .sort((a, b) => {
              const timeToMinutes = (timeStr) => {
                if (!timeStr || timeStr === '--:--') return -1;
                const [hours, minutes] = timeStr.split(':').map(Number);
                return hours * 60 + minutes;
              };
              return timeToMinutes(a.time) - timeToMinutes(b.time);
            })
            .map((ct, index) => (
              <Box key={`friday-${index}`} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', direction: 'ltr' }}>
                <Typography sx={{
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: heritageColors.navy,
                }}>
                  {ct.time}
                </Typography>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ color: heritageColors.slate }}>{ct.label}</Typography>
                  {showTimeRule && formatShabbatTimeRule(ct) && (
                    <Typography sx={{ fontSize: '0.7rem', color: heritageColors.slate, opacity: 0.6, lineHeight: 1, direction: 'rtl' }}>
                      {formatShabbatTimeRule(ct)}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}

          {/* אחר כך זמני שבת */}
          {processedCustomTimes
            .filter(ct => (ct.day || 'friday') === 'shabbat')
            .sort((a, b) => {
              const timeToMinutes = (timeStr) => {
                if (!timeStr || timeStr === '--:--') return -1;
                const [hours, minutes] = timeStr.split(':').map(Number);
                return hours * 60 + minutes;
              };
              return timeToMinutes(a.time) - timeToMinutes(b.time);
            })
            .map((ct, index) => (
              <Box key={`shabbat-${index}`} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', direction: 'ltr' }}>
                <Typography sx={{
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: heritageColors.navy,
                }}>
                  {ct.time}
                </Typography>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ color: heritageColors.slate }}>{ct.label}</Typography>
                  {showTimeRule && formatShabbatTimeRule(ct) && (
                    <Typography sx={{ fontSize: '0.7rem', color: heritageColors.slate, opacity: 0.6, lineHeight: 1, direction: 'rtl' }}>
                      {formatShabbatTimeRule(ct)}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
        </Stack>
      )}
    </Box>
  );
};

export default Times;
